import { isFunction } from 'lodash-es';

import type { CompatibleStorageValue, MigrationStrategy, Parse, Stringify, V2Item } from './types';

const V2_KEY_PREFIX = 'csno.v2.';

export const migrateValue = <T extends CompatibleStorageValue>({
  value,
  emptyValue,
  migrationStrategy,
}: {
  value: T;
  emptyValue: unknown;
  migrationStrategy: MigrationStrategy<T>;
}): T => {
  if (isFunction(migrationStrategy)) {
    return migrationStrategy(value);
  }
  switch (migrationStrategy) {
    case 'none':
      return value;
    case 'mergeWithDefault':
      return typeof value === 'object' && typeof emptyValue === 'object'
        ? { ...emptyValue, ...value }
        : value;
  }
};

function isV2Key(key: string) {
  return key.startsWith(V2_KEY_PREFIX);
}

export const defaultStringify = JSON.stringify;

export function defaultParse<T>(value: string | null): T | null {
  if (value === null) return null;

  try {
    return JSON.parse(value) as T;
  } catch {
    return value as T;
  }
}

// Remove expired keys from local storage
export function pruneStorage() {
  const now = new Date();
  const keys = Object.keys(localStorage);
  const expiredKeys = keys.filter((key) => {
    if (!isV2Key(key)) {
      // Remove v1 keys if there's a matching v2 key, or if it's been a month since this was deployed
      return now >= new Date('2024-07-15') || keys.includes(getVersionedKey(key, 2));
    }

    const value = localStorage.getItem(key);
    const parsedValue = defaultParse<V2Item<CompatibleStorageValue>>(value);

    if (parsedValue === null || parsedValue === undefined) {
      return false;
    }

    return now.getTime() > parsedValue.expiresAt;
  });

  expiredKeys.forEach((key) => {
    localStorage.removeItem(key);
  });
}

// 4 weeks
export const DEFAULT_VALUE_DURATION = 4 * 7 * 24 * 60 * 60 * 1000;

export function getVersionedKey(unversionedKey: string, version: 1 | 2) {
  return version === 2 ? `${V2_KEY_PREFIX}${unversionedKey}` : unversionedKey;
}

interface SetValueForKeyOptions<T> {
  unversionedKey: string;
  version: 1 | 2;
  value: T;
  duration: number;
  stringify: Stringify;
}

export function setValueForKey<T extends CompatibleStorageValue>({
  unversionedKey,
  version,
  value,
  duration,
  stringify,
}: SetValueForKeyOptions<T>) {
  const versionedKey = getVersionedKey(unversionedKey, version);
  const v2Value: V2Item<T> = {
    value,
    expiresAt: new Date().getTime() + duration,
  };

  localStorage.setItem(versionedKey, stringify(v2Value));
}

interface GetValueForKeyOptions {
  unversionedKey: string;
  version: 1 | 2;
  duration: number;
  parse: Parse;
  stringify: Stringify;
}

export function getValueForKey<T extends CompatibleStorageValue>({
  unversionedKey,
  version,
  duration,
  parse,
  stringify,
}: GetValueForKeyOptions): T | null {
  const v1Value = parse<T>(localStorage.getItem(unversionedKey));
  switch (version) {
    case 1: {
      return v1Value;
    }
    case 2: {
      const versionedKey = getVersionedKey(unversionedKey, version);
      const v2Value = parse<V2Item<T>>(localStorage.getItem(versionedKey));
      if (v2Value === null && v1Value !== null) {
        // Upgrade to v2 storage
        // Remove v1 first
        localStorage.removeItem(unversionedKey);
        // Write new key afterwards (this order has a better chance at avoiding storage limit)
        setValueForKey({ unversionedKey, version, value: v1Value, duration, stringify });

        return v1Value;
      }

      return v2Value?.value ?? null;
    }
    default:
      return null;
  }
}
